import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";
import { prodApi, fmtEur, KINDS, labelOf, errMsg } from "./prodApi";
import { useProd } from "./ProdApp";
import { PButton, PTitle, PBadge, PCard, PToggle, PEmpty } from "./ProdUi";
import { NodeForm, TrimForm } from "./ProdTreeForms";

const KIND_TONE = { meat: "blue", processed: "violet", byproduct: "amber", waste: "red" };

function NodeRow({ node, depth, children, canEdit, onEdit, onAdd }) {
  const [open, setOpen] = useState(depth < 3);
  const has = children.length > 0;
  return (
    <div>
      <div className={`flex items-center gap-2 py-2 pr-2 rounded-xl hover:bg-slate-800/70 ${!node.active ? "opacity-50" : ""}`} style={{ paddingLeft: `${depth * 18 + 4}px` }} data-testid={`prod-node-${node.code}`}>
        <button onClick={() => setOpen(!open)} className="w-11 h-11 flex items-center justify-center text-slate-400 rounded-lg hover:bg-slate-700 shrink-0" disabled={!has} data-testid={`prod-node-toggle-${node.code}`}>
          {has ? (open ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />) : <span className="w-1.5 h-1.5 rounded-full bg-slate-600" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-white">{node.name}</span>
            <span className="text-[11px] text-slate-500 font-mono">{node.code}</span>
            <PBadge tone={KIND_TONE[node.kind]}>{labelOf(KINDS, node.kind)}</PBadge>
          </div>
          <p className="text-xs text-slate-400 tabular-nums">
            {node.kind === "meat" || node.kind === "processed" ? <>coeff. <b className="text-slate-200">{node.coefficient}</b> · prezzo <b className="text-slate-200">{fmtEur(node.sale_price_per_kg)}/kg</b></> : <>valore <b className={node.value_per_kg < 0 ? "text-red-300" : "text-emerald-300"}>{fmtEur(node.value_per_kg)}/kg</b></>}
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-1 shrink-0">
            <button onClick={() => onAdd(node)} className="w-11 h-11 rounded-lg flex items-center justify-center text-slate-300 hover:bg-slate-700" title="Aggiungi sotto-taglio" data-testid={`prod-node-add-${node.code}`}><Plus className="w-5 h-5" /></button>
            <button onClick={() => onEdit(node)} className="w-11 h-11 rounded-lg flex items-center justify-center text-slate-300 hover:bg-slate-700" title="Modifica" data-testid={`prod-node-edit-${node.code}`}><Pencil className="w-5 h-5" /></button>
          </div>
        )}
      </div>
      {open && has && <div className="border-l border-slate-700/60 ml-[26px]">{children.map((c) => c)}</div>}
    </div>
  );
}

export default function ProdTree() {
  const { nodes, trims, user, reload } = useProd();
  const [tab, setTab] = useState("tree");
  const [editing, setEditing] = useState(null);
  const [trimEditing, setTrimEditing] = useState(null);
  const canEdit = !!user?.is_manager;

  const byParent = useMemo(() => {
    const m = {};
    nodes.forEach((n) => { (m[n.parent_id || "root"] ||= []).push(n); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)));
    return m;
  }, [nodes]);

  const render = (parent, depth) => (byParent[parent] || []).map((n) => (
    <NodeRow key={n.id} node={n} depth={depth} canEdit={canEdit} onEdit={setEditing} onAdd={(p) => setEditing({ parent_id: p.id, kind: p.kind })}>
      {render(n.id, depth + 1)}
    </NodeRow>
  ));

  const removeTrim = async (t) => {
    if (!window.confirm(`Eliminare la rifilatura "${t.name}"?`)) return;
    try { await prodApi.delete(`/production/trim-levels/${t.id}`); await reload(); toast.success("Rifilatura eliminata"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="prod-tree-page">
      <PTitle kicker="Configurazione" title="Albero tagli e rifilature" right={canEdit && tab === "tree" && <PButton variant="primary" onClick={() => setEditing({})} data-testid="prod-node-new-btn"><Plus className="w-5 h-5" /> Nuovo taglio</PButton>}>
        Struttura gerarchica configurabile: ogni voce può essere materia prima o output di una ricetta.
      </PTitle>
      <PToggle className="max-w-md mb-5" value={tab} onChange={setTab} testIdPrefix="prod-tree-tab" options={[{ value: "tree", label: `Albero (${nodes.length})` }, { value: "trims", label: `Rifilature (${trims.length})` }]} />

      {tab === "tree" && (
        nodes.length ? <PCard className="!p-2 md:!p-3">{render("root", 0)}</PCard>
          : <PEmpty title="Nessun taglio configurato" text="Crea la materia prima di partenza (es. Pistola) e poi i tagli figli, oppure carica l'esempio dalla Dashboard." />
      )}

      {tab === "trims" && (
        <div className="space-y-3">
          {canEdit && <PButton variant="primary" onClick={() => setTrimEditing({})} data-testid="prod-trim-new-btn"><Plus className="w-5 h-5" /> Nuova rifilatura</PButton>}
          {trims.map((t) => (
            <PCard key={t.id} className="flex items-center gap-3" data-testid={`prod-trim-${t.code}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white">{t.name} <span className="text-[11px] text-slate-500 font-mono ml-1">{t.code}</span></p>
                <p className="text-xs text-slate-400">{t.description}</p>
              </div>
              {canEdit && <>
                <button onClick={() => setTrimEditing(t)} className="w-11 h-11 rounded-lg flex items-center justify-center text-slate-300 hover:bg-slate-700" data-testid={`prod-trim-edit-${t.code}`}><Pencil className="w-5 h-5" /></button>
                <button onClick={() => removeTrim(t)} className="w-11 h-11 rounded-lg flex items-center justify-center text-red-300 hover:bg-slate-700" data-testid={`prod-trim-delete-${t.code}`}><Trash2 className="w-5 h-5" /></button>
              </>}
            </PCard>
          ))}
          {!trims.length && <PEmpty title="Nessun livello di rifilatura" text="Es. Grezzo / Standard / PAD: ogni livello può avere resa, tempo e prezzo diversi." />}
        </div>
      )}

      <NodeForm node={editing} onClose={() => setEditing(null)} />
      <TrimForm trim={trimEditing} onClose={() => setTrimEditing(null)} />
    </div>
  );
}
